import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  updateDoc,
  getDocs
} from "firebase/firestore";
import { db } from "../../firebase";
import { DataGrid } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Button, CircularProgress, Snackbar, Alert, FormControlLabel, Switch, Chip, Tooltip } from "@mui/material";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import NotificationHelper from "./NotificationHelper";

// Iconos para diferentes módulos
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder";
import RouteIcon from "@mui/icons-material/Route";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ForumIcon from "@mui/icons-material/Forum";

/**
 * Componente para mostrar y gestionar la lista de notificaciones
 */
const NotificationsList = ({ adminData, currentUser, currentService }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("all"); // "all", "unread", "read"
  const [operationLoading, setOperationLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  // Nuevo estado para controlar si los administradores ven todas las notificaciones
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  // Estados para mapeo de IDs a nombres
  const [clientesMap, setClientesMap] = useState({});
  const [tiendasMap, setTiendasMap] = useState({});
  const [loadingMappings, setLoadingMappings] = useState(false);

  // Determinar si el usuario es administrador
  const isAdmin = adminData?.tipoUsuario?.includes("administrador");

  // Efecto para cargar los mapeos de clientes y tiendas
  useEffect(() => {
    if (!currentService?.id) return;

    const loadClientesAndTiendas = async () => {
      setLoadingMappings(true);
      try {
        console.log("Cargando mapeos de clientes y tiendas...");

        // Cargar clientes
        const clientesRef = collection(db, "servicios", currentService.id, "clientes");
        const clientesSnapshot = await getDocs(clientesRef);

        const clientesData = {};
        clientesSnapshot.docs.forEach(doc => {
          clientesData[doc.id] = doc.data().nombre || `Cliente ${doc.id}`;
        });

        console.log(`Cargados ${Object.keys(clientesData).length} clientes`);
        setClientesMap(clientesData);

        // Cargar tiendas/servicios
        const tiendasRef = collection(db, "servicios", currentService.id, "tiendas");
        const tiendasSnapshot = await getDocs(tiendasRef);

        const tiendasData = {};
        tiendasSnapshot.docs.forEach(doc => {
          const tiendaData = doc.data();
          tiendasData[doc.id] = tiendaData.nombre || ` ${tiendaData.numTienda || doc.id}`;
        });

        console.log(`Cargadas ${Object.keys(tiendasData).length} tiendas/servicios`);
        setTiendasMap(tiendasData);
      } catch (error) {
        console.error("Error al cargar mapeos de clientes y tiendas:", error);
      } finally {
        setLoadingMappings(false);
      }
    };

    loadClientesAndTiendas();
  }, [currentService]);

  // Función para enriquecer las notificaciones con nombres
  const enrichNotifications = (notificationsData) => {
    return notificationsData.map(notification => {
      const enriched = { ...notification };

      // Añadir nombre del cliente si existe
      if (notification.clienteId && clientesMap[notification.clienteId]) {
        enriched.clienteNombre = clientesMap[notification.clienteId];
      }

      // Añadir nombre de la tienda si existe
      if (notification.tiendaId && tiendasMap[notification.tiendaId]) {
        enriched.tiendaNombre = tiendasMap[notification.tiendaId];
      }

      return enriched;
    });
  };

  // Efecto para cargar y suscribirse a las notificaciones
  useEffect(() => {
    if (!currentUser?.email || !currentService?.id) return;

    let unsubscribe = () => { };

    // Función para iniciar la suscripción
    const startSubscription = () => {
      // Limpiar cualquier suscripción anterior
      unsubscribe();

      // Restablecer estado de carga
      setLoading(true);

      // Obtener referencia a las notificaciones del dashboard
      const notificationsRef = collection(db, "servicios", currentService.id, "notificaciones_dashboard");

      // Consulta para obtener todas las notificaciones si es administrador y ha activado ver todas, 
      // o solo las del usuario actual
      let q;

      console.log("Estado showAllNotifications:", showAllNotifications);
      console.log("Es administrador:", isAdmin);
      console.log("Usuario actual:", currentUser.email);

      if (isAdmin && showAllNotifications) {
        // Administradores pueden ver todas las notificaciones si activan la opción
        q = query(
          notificationsRef,
          orderBy("fecha", "desc")
        );
        console.log("Mostrando todas las notificaciones (modo administrador)");
      } else {
        // Por defecto, todos los usuarios (incluso admins) solo ven las dirigidas a ellos o a "todos"
        q = query(
          notificationsRef,
          where("destinatario", "in", [currentUser.email, "todos"]),
          orderBy("fecha", "desc")
        );
        console.log("Mostrando solo notificaciones para:", currentUser.email);
      }

      // Suscribirse a los cambios en tiempo real
      unsubscribe = onSnapshot(q, (snapshot) => {
        const notificationsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          // Propiedades adicionales para nombres legibles (inicialmente null)
          clienteNombre: null,
          tiendaNombre: null
        }));

        console.log(`Recibidas ${notificationsData.length} notificaciones`);

        // Enriquecer con nombres si los mapeos están disponibles
        const enrichedData = enrichNotifications(notificationsData);
        setNotifications(enrichedData);
        setLoading(false);
      }, (error) => {
        console.error("Error al obtener notificaciones:", error);
        setLoading(false);
      });
    };

    // Iniciar suscripción
    startSubscription();

    // Limpiar suscripción al desmontar
    return () => unsubscribe();
  }, [currentUser, currentService, adminData, showAllNotifications, isAdmin, clientesMap, tiendasMap]);

  // Manejar cambio en el toggle de ver todas las notificaciones
  const handleToggleAllNotifications = (event) => {
    const newValue = event.target.checked;
    console.log("Cambiando showAllNotifications a:", newValue);

    // Actualizar estado
    setShowAllNotifications(newValue);

    // Forzar una recarga de la tabla
    setLoading(true);

    // Pequeño retraso para asegurar que la UI muestra el estado de carga
    setTimeout(() => {
      // El efecto se activará con el nuevo valor de showAllNotifications
    }, 100);
  };

  // Función para formatear la fecha
  const formatDate = (timestamp) => {
    if (!timestamp) return "";

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, "d 'de' MMMM, HH:mm", { locale: es });
  };

  // Función para mostrar mensajes en el snackbar
  const showMessage = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Función para marcar una notificación como leída
  const markAsRead = async (id) => {
    if (operationLoading) return;

    setOperationLoading(true);
    try {
      const result = await NotificationHelper.markAsRead(id, currentService.id);
      if (result.success) {
        showMessage("Notificación marcada como leída");
      } else {
        throw new Error(result.error || "Error desconocido");
      }
    } catch (error) {
      console.error("Error al marcar como leída:", error);
      showMessage("Error al marcar la notificación como leída", "error");
    } finally {
      setOperationLoading(false);
    }
  };

  // Función para marcar una notificación como no leída
  const markAsUnread = async (id) => {
    if (operationLoading) return;

    setOperationLoading(true);
    try {
      // Actualizamos directamente en Firestore ya que no hay una función Cloud para esto
      const notificationRef = doc(db, "servicios", currentService.id, "notificaciones_dashboard", id);
      await updateDoc(notificationRef, {
        leida: false
      });
      showMessage("Notificación marcada como no leída");
    } catch (error) {
      console.error("Error al marcar como no leída:", error);
      showMessage("Error al marcar la notificación como no leída", "error");
    } finally {
      setOperationLoading(false);
    }
  };

  // Función para eliminar una notificación
  const deleteNotification = async (id) => {
    if (operationLoading) return;

    if (!window.confirm("¿Estás seguro de eliminar esta notificación?")) {
      return;
    }

    setOperationLoading(true);
    try {
      const result = await NotificationHelper.deleteNotification(id, currentService.id);
      if (result.success) {
        showMessage("Notificación eliminada correctamente");
      } else {
        throw new Error(result.error || "Error desconocido");
      }
    } catch (error) {
      console.error("Error al eliminar notificación:", error);
      showMessage("Error al eliminar la notificación", "error");
    } finally {
      setOperationLoading(false);
    }
  };

  // Función para marcar todas las notificaciones como leídas
  const markAllAsRead = async () => {
    if (operationLoading) return;

    setOperationLoading(true);
    try {
      const result = await NotificationHelper.markAllAsRead(currentService.id);
      if (result.success) {
        const count = result.data?.count || 0;
        showMessage(`${count} notificaciones marcadas como leídas`);
      } else {
        throw new Error(result.error || "Error desconocido");
      }
    } catch (error) {
      console.error("Error al marcar todas como leídas:", error);
      showMessage("Error al marcar todas las notificaciones como leídas", "error");
    } finally {
      setOperationLoading(false);
    }
  };

  // Función para obtener el nombre del módulo
  const getModuleName = (modulo) => {
    switch (modulo?.toLowerCase()) {
      case 'documentos':
        return 'Documentos';
      case 'cuadrantes':
        return 'Cuadrantes';
      case 'vigilantes':
        return 'Usuarios';
      case 'clientes':
        return 'Clientes';
      case 'servicios':
      case 'tiendas':
        return 'Servicios';
      case 'control horario':
      case 'jornadas':
        return 'Control Horario';
      case 'control de rondas':
      case 'controlrondas':
        return 'Control de Rondas';
      case 'buzón anónimo':
      case 'buzonanonimo':
        return 'Buzón Anónimo';
      default:
        return modulo;
    }
  };

  // Función para obtener el icono según el módulo
  const getModuleIcon = (modulo) => {
    switch (modulo?.toLowerCase()) {
      case 'documentos':
        return <DescriptionOutlinedIcon fontSize="small" />;
      case 'cuadrantes':
        return <CalendarMonthIcon fontSize="small" />;
      case 'vigilantes':
        return <BadgeOutlinedIcon fontSize="small" />;
      case 'clientes':
        return <GroupsOutlinedIcon fontSize="small" />;
      case 'servicios':
      case 'tiendas':
        return <StoreOutlinedIcon fontSize="small" />;
      case 'control horario':
      case 'jornadas':
        return <QueryBuilderIcon fontSize="small" />;
      case 'control de rondas':
      case 'controlrondas':
        return <RouteIcon fontSize="small" />;
      case 'buzón anónimo':
      case 'buzonanonimo':
        return <ForumIcon fontSize="small" />;
      default:
        return null;
    }
  };

  // Filtrar notificaciones según la pestaña seleccionada y filtros personalizados
  const filteredNotifications = notifications.filter(notification => {
    if (selectedTab === "all") return true;
    if (selectedTab === "unread") return !notification.leida;
    if (selectedTab === "read") return notification.leida;
    return true;
  });

  // Columnas para la tabla de notificaciones
  const columns = [
    {
      field: "fecha",
      headerName: "Fecha",
      width: 180,
      renderCell: (params) => formatDate(params.row.fecha),
    },
    {
      field: "titulo",
      headerName: "Título",
      width: 200,
      renderCell: (params) => (
        <div className={`title-cell ${params.row.leida ? "" : "unread"}`}>
          {params.row.titulo}
        </div>
      ),
    },
    {
      field: "mensaje",
      headerName: "Mensaje",
      width: 300,
    },
    // Columna para cliente con nombre
    {
      field: "clienteId",
      headerName: "Cliente",
      width: 180,
      renderCell: (params) => {
        // Si existe un nombre de cliente, lo mostramos, de lo contrario mostramos el ID o N/A
        const displayText = params.row.clienteNombre || params.value || "N/A";
        const tooltipText = params.row.clienteNombre && params.value ? `ID: ${params.value}` : "";

        return (
          <Tooltip title={tooltipText} arrow placement="top">
            <div className="cliente-cell">
              <GroupsOutlinedIcon fontSize="small" style={{ marginRight: '5px' }} />
              <span>{displayText}</span>
            </div>
          </Tooltip>
        );
      },
    },
    // Columna para servicio/tienda con nombre
    {
      field: "tiendaId",
      headerName: "Servicio/Tienda",
      width: 180,
      renderCell: (params) => {
        // Si existe un nombre de tienda, lo mostramos, de lo contrario mostramos el ID o N/A
        const displayText = params.row.tiendaNombre || params.value || "N/A";
        const tooltipText = params.row.tiendaNombre && params.value ? `ID: ${params.value}` : "";

        return (
          <Tooltip title={tooltipText} arrow placement="top">
            <div className="tienda-cell">
              <StoreOutlinedIcon fontSize="small" style={{ marginRight: '5px' }} />
              <span>{displayText}</span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "modulo",
      headerName: "Módulo",
      width: 150,
      renderCell: (params) => (
        <div className="module-cell">
          {getModuleIcon(params.row.modulo)}
          <span>{getModuleName(params.row.modulo)}</span>
        </div>
      ),
    },
    // Mostrar destinatario solo para administradores que han activado ver todas
    ...(isAdmin && showAllNotifications ? [{
      field: "destinatario",
      headerName: "Destinatario",
      width: 180,
      renderCell: (params) => (
        <div className="destinatario-cell">
          <AccountCircleIcon fontSize="small" style={{ marginRight: '5px' }} />
          {params.value === currentUser.email ? (
            <Chip label="Para ti" color="primary" size="small" />
          ) : params.value === "todos" ? (
            <Chip label="Todos" color="secondary" size="small" />
          ) : (
            <span>{params.value}</span>
          )}
        </div>
      ),
    }] : []),
    {
      field: "leida",
      headerName: "Estado",
      width: 120,
      renderCell: (params) => (
        <span className={`status ${params.row.leida ? "read" : "unread"}`}>
          {params.row.leida ? "Leída" : "No leída"}
        </span>
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 150,
      renderCell: (params) => (
        <div className="cellAction">
          {params.row.leida ? (
            <div
              className="markUnreadButton"
              onClick={() => markAsUnread(params.row.id)}
              title="Marcar como no leída"
            >
              <CheckCircleOutlineIcon />
            </div>
          ) : (
            <div
              className="markReadButton"
              onClick={() => markAsRead(params.row.id)}
              title="Marcar como leída"
            >
              <CheckCircleOutlineIcon />
            </div>
          )}
          <div
            className="deleteButton"
            onClick={() => deleteNotification(params.row.id)}
            title="Eliminar notificación"
          >
            <DeleteOutlineIcon />
          </div>
          {params.row.link && (
            <div className="viewButton">
              <Link
                to={params.row.link}
                style={{ textDecoration: "none" }}
                onClick={() => !params.row.leida && markAsRead(params.row.id)}
              >
                <VisibilityIcon />
              </Link>
            </div>
          )}
        </div>
      ),
    },
  ];

  // Determinar si estamos cargando datos
  const isDataLoading = loading || loadingMappings;

  return (
    <>
      <div className="notification-actions">
        {/* Toggle para administradores para ver todas las notificaciones */}
        {isAdmin && (
          <FormControlLabel
            control={
              <Switch
                checked={showAllNotifications}
                onChange={handleToggleAllNotifications}
                color="primary"
              />
            }
            label="Ver todas las notificaciones"
            style={{ marginRight: '16px' }}
          />
        )}

        {filteredNotifications.some(n => !n.leida) && (
          <Button
            variant="outlined"
            onClick={markAllAsRead}
            className="markAllReadButton"
            disabled={operationLoading}
          >
            {operationLoading ? (
              <CircularProgress size={20} style={{ marginRight: '8px' }} />
            ) : null}
            Marcar todas como leídas
          </Button>
        )}
      </div>

      <div className="notification-tabs">
        <button
          className={`tab ${selectedTab === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedTab('all')}
        >
          Todas
        </button>
        <button
          className={`tab ${selectedTab === 'unread' ? 'active' : ''}`}
          onClick={() => setSelectedTab('unread')}
        >
          No leídas {notifications.filter(n => !n.leida).length > 0 &&
            <span className="badge">{notifications.filter(n => !n.leida).length}</span>}
        </button>
        <button
          className={`tab ${selectedTab === 'read' ? 'active' : ''}`}
          onClick={() => setSelectedTab('read')}
        >
          Leídas
        </button>
      </div>

      <div className="notificationsTable">
        {notifications.length === 0 ? (
          <div className="no-notifications">
            <p>No hay notificaciones disponibles</p>
          </div>
        ) : (
          <DataGrid
            rows={filteredNotifications}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            checkboxSelection={false}
            disableSelectionOnClick
            autoHeight
            loading={isDataLoading}
            getRowClassName={(params) => params.row.leida ? "" : "unread-row"}
          />
        )}
      </div>

      {/* Snackbar para mensajes */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationsList;